import { useState, useMemo } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiPlus, FiRefreshCw, FiTrash2, FiX, FiSearch,
  FiGlobe, FiMap, FiLink, FiZap, FiChevronDown,
} from "react-icons/fi";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

const INITIAL_LINKS = [
  { id: 1, url: "https://nexagpt.ai",                            type: "WEB", status: "Trained",  lastTrained: "2 days ago",  chars: 4820 },
  { id: 2, url: "https://nexagpt.ai/features",                   type: "WEB", status: "Trained",  lastTrained: "2 days ago",  chars: 6230 },
  { id: 3, url: "https://nexagpt.ai/pricing",                    type: "WEB", status: "Trained",  lastTrained: "2 days ago",  chars: 3140 },
  { id: 4, url: "https://nexagpt.ai/integrations",               type: "WEB", status: "Trained",  lastTrained: "1 day ago",   chars: 5580 },
  { id: 5, url: "https://nexagpt.ai/faqs",                       type: "WEB", status: "Trained",  lastTrained: "1 day ago",   chars: 7920 },
  { id: 6, url: "https://nexagpt.ai/features/train-on-website",  type: "WEB", status: "Pending",  lastTrained: "—",           chars: 0    },
  { id: 7, url: "https://nexagpt.ai/features/train-on-pdf",      type: "WEB", status: "Failed",   lastTrained: "3 days ago",  chars: 0    },
  { id: 8, url: "https://nexagpt.ai/solutions/customer-support", type: "WEB", status: "Trained",  lastTrained: "3 days ago",  chars: 5440 },
];

const PAGE_SIZE = 8;

function ContentPageSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-56" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <ShimmerTable rows={8} cols={6} />
    </div>
  );
}

export default function LinksPage() {
  const [links, setLinks] = useState(INITIAL_LINKS);
  const loading = usePageLoader();
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [addMode, setAddMode] = useState(null); // null | "single" | "crawl" | "sitemap"

  /* Single URL */
  const [newUrl, setNewUrl] = useState("");

  /* Crawl Domain */
  const [crawlDomain, setCrawlDomain] = useState("");
  const [crawlMaxPages, setCrawlMaxPages] = useState(25);
  const [crawlResults, setCrawlResults] = useState(null);
  const [crawlLoading, setCrawlLoading] = useState(false);

  /* Sitemap Import */
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [sitemapLoading, setSitemapLoading] = useState(false);

  /* ── Derived counts ── */
  const counts = useMemo(() => ({
    total:   links.length,
    trained: links.filter(l => l.status === "Trained").length,
    pending: links.filter(l => l.status === "Pending").length,
    failed:  links.filter(l => l.status === "Failed").length,
    chars:   links.reduce((s, l) => s + l.chars, 0),
  }), [links]);

  /* ── Filtered + paginated ── */
  const filtered = useMemo(() => links.filter(l => {
    const okFilter = filter === "all" || l.status === filter;
    const okSearch = !search || l.url.toLowerCase().includes(search.toLowerCase());
    return okFilter && okSearch;
  }), [links, filter, search]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds     = paginated.map(l => l.id);
  const allPageSel  = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  /* ── Helpers ── */
  const changeFilter = (f) => { setFilter(f); setPage(1); setSelectedIds([]); };
  const changeSearch = (s) => { setSearch(s); setPage(1); };
  const toggleSel    = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllSel = () => {
    if (allPageSel) setSelectedIds(p => p.filter(id => !pageIds.includes(id)));
    else setSelectedIds(p => [...new Set([...p, ...pageIds])]);
  };
  const fmtChars = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  const simulateRetrain = (ids, cb) => {
    setLinks(p => p.map(l => ids.includes(l.id) ? { ...l, status: "Processing" } : l));
    setTimeout(() => {
      setLinks(p => p.map(l => ids.includes(l.id)
        ? { ...l, status: "Trained", lastTrained: "Just now", chars: l.chars || (Math.floor(Math.random() * 4000) + 1000) }
        : l
      ));
      cb?.();
    }, 2000);
  };

  /* ── Handlers ── */
  const handleAdd = () => {
    const url = newUrl.trim();
    if (!url || !url.startsWith("http")) { toast.error("Enter a valid URL starting with http(s)://"); return; }
    if (links.some(l => l.url === url)) { toast.error("This URL is already added."); return; }
    setLinks(p => [{ id: Date.now(), url, type: "WEB", status: "Pending", lastTrained: "—", chars: 0 }, ...p]);
    setNewUrl(""); setAddMode(null); setPage(1);
    toast.success("URL added and queued for training.");
  };

  const handleDelete = (id) => {
    setLinks(p => p.filter(l => l.id !== id));
    setSelectedIds(p => p.filter(x => x !== id));
    toast.success("Link removed.");
  };

  const handleRetrain = (id) => {
    simulateRetrain([id], () => toast.success("Link retrained successfully."));
  };

  const handleBulkDelete = () => {
    const n = selectedIds.length;
    setLinks(p => p.filter(l => !selectedIds.includes(l.id)));
    setSelectedIds([]);
    toast.success(`${n} link${n > 1 ? "s" : ""} deleted.`);
  };

  const handleBulkRetrain = () => {
    const ids = [...selectedIds];
    simulateRetrain(ids, () => {
      setSelectedIds([]);
      toast.success(`${ids.length} link${ids.length > 1 ? "s" : ""} retrained.`);
    });
  };

  const handleTrainAllPending = () => {
    const ids = links.filter(l => l.status === "Pending").map(l => l.id);
    if (!ids.length) return;
    simulateRetrain(ids, () => toast.success(`${ids.length} pending link${ids.length > 1 ? "s" : ""} trained.`));
  };

  const handleCrawl = async () => {
    if (!crawlDomain.trim()) { toast.error("Enter a domain to crawl."); return; }
    setCrawlLoading(true); setCrawlResults(null);
    await new Promise(r => setTimeout(r, 1600));
    const domain = crawlDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
    const PATHS = ["/", "/features", "/pricing", "/about", "/blog", "/contact", "/faqs", "/integrations", "/docs", "/support", "/terms", "/privacy"];
    const results = PATHS
      .slice(0, Math.min(crawlMaxPages, PATHS.length))
      .map(p => ({ url: `https://${domain}${p}`, chars: Math.floor(Math.random() * 5000) + 800 }))
      .filter(r => !links.some(l => l.url === r.url));
    setCrawlResults(results);
    setCrawlLoading(false);
    if (!results.length) toast("All discovered URLs are already added.", { icon: "ℹ️" });
  };

  const handleAddCrawlResults = () => {
    if (!crawlResults?.length) return;
    const newLinks = crawlResults.map((r, i) => ({
      id: Date.now() + i, url: r.url, type: "WEB", status: "Pending", lastTrained: "—", chars: 0,
    }));
    setLinks(p => [...newLinks, ...p]);
    setCrawlResults(null); setCrawlDomain(""); setAddMode(null);
    toast.success(`${newLinks.length} URLs added and queued for training.`);
  };

  const handleSitemapImport = async () => {
    if (!sitemapUrl.trim() || !sitemapUrl.startsWith("http")) { toast.error("Enter a valid sitemap URL."); return; }
    setSitemapLoading(true);
    await new Promise(r => setTimeout(r, 1400));
    const domain = sitemapUrl.replace(/^https?:\/\//, "").split("/")[0];
    const PATHS = ["/", "/features", "/pricing", "/blog/post-1", "/blog/post-2", "/blog/post-3", "/about", "/contact"];
    const newLinks = PATHS
      .map((p, i) => ({ id: Date.now() + i, url: `https://${domain}${p}`, type: "WEB", status: "Pending", lastTrained: "—", chars: 0 }))
      .filter(n => !links.some(l => l.url === n.url));
    setLinks(p => [...newLinks, ...p]);
    setSitemapUrl(""); setAddMode(null); setSitemapLoading(false);
    toast.success(`${newLinks.length} URLs imported from sitemap.`);
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Page header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Website Links</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.total} URLs · {counts.trained} trained · {fmtChars(counts.chars)} chars indexed
          </p>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <button
              onClick={handleTrainAllPending}
              className="flex items-center gap-2 text-sm border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition"
            >
              <FiZap size={14} /> Train {counts.pending} Pending
            </button>
          )}
          <AddMenu addMode={addMode} setAddMode={(m) => { setAddMode(m); setCrawlResults(null); }} />
        </div>
      </div>

      {loading ? <ContentPageSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50">

        {/* ── Add content panel ── */}
        {addMode && (
          <div className="bg-white border-b shadow-sm">
            <div className="px-6 pt-4 pb-5">
              {/* Mode tabs */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium">
                {[
                  { key: "single",  icon: <FiLink size={13} />,  label: "Single URL" },
                  { key: "crawl",   icon: <FiGlobe size={13} />, label: "Crawl Domain" },
                  { key: "sitemap", icon: <FiMap size={13} />,   label: "Import Sitemap" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    onClick={() => { setAddMode(key); setCrawlResults(null); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 transition ${
                      addMode === key ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
                </div>
                <button
                  onClick={() => { setAddMode(null); setCrawlResults(null); }}
                  className="ml-auto p-1.5 text-gray-400 hover:text-gray-700"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* Single URL */}
              {addMode === "single" && (
                <div className="flex gap-3 max-w-2xl">
                  <input
                    type="url" value={newUrl}
                    onChange={e => setNewUrl(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleAdd()}
                    placeholder="https://yourwebsite.com/page"
                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    autoFocus
                  />
                  <button onClick={handleAdd} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90">
                    Add URL
                  </button>
                </div>
              )}

              {/* Crawl Domain */}
              {addMode === "crawl" && (
                <div className="max-w-2xl space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text" value={crawlDomain}
                      onChange={e => setCrawlDomain(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <button
                      onClick={handleCrawl}
                      disabled={crawlLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-70"
                    >
                      {crawlLoading
                        ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <FiGlobe size={14} />}
                      {crawlLoading ? "Crawling…" : "Fetch Links"}
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 whitespace-nowrap w-28">Max pages: {crawlMaxPages}</span>
                    <input
                      type="range" min={1} max={100} value={crawlMaxPages}
                      onChange={e => setCrawlMaxPages(Number(e.target.value))}
                      className="w-40 accent-primary"
                    />
                  </div>
                  {crawlResults !== null && crawlResults.length > 0 && (
                    <div className="border rounded-xl overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-800">{crawlResults.length} new URLs found</p>
                        <button
                          onClick={handleAddCrawlResults}
                          className="text-sm text-primary font-medium hover:underline"
                        >
                          Add All ({crawlResults.length})
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto divide-y">
                        {crawlResults.map((r, i) => (
                          <div key={i} className="px-4 py-2 flex items-center justify-between">
                            <span className="text-sm text-gray-700 truncate">{r.url}</span>
                            <span className="text-xs text-gray-400 ml-3 shrink-0">~{fmtChars(r.chars)} chars</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sitemap Import */}
              {addMode === "sitemap" && (
                <div className="space-y-2 max-w-2xl">
                  <div className="flex gap-3">
                    <input
                      type="url" value={sitemapUrl}
                      onChange={e => setSitemapUrl(e.target.value)}
                      placeholder="https://yourwebsite.com/sitemap.xml"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <button
                      onClick={handleSitemapImport}
                      disabled={sitemapLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-70"
                    >
                      {sitemapLoading
                        ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <FiMap size={14} />}
                      {sitemapLoading ? "Importing…" : "Import"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">NexaGPT will parse your sitemap and add all listed URLs as training sources.</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-6 space-y-4">

          {/* ── KPI cards ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total URLs"    value={counts.total}            color="text-gray-900" />
            <KpiCard label="Trained"       value={counts.trained}          color="text-green-600" dot="bg-green-500" />
            <KpiCard label="Pending / Failed" value={`${counts.pending} / ${counts.failed}`} color="text-yellow-600" dot="bg-yellow-500" />
            <KpiCard label="Chars Indexed" value={fmtChars(counts.chars)}  color="text-primary" />
          </div>

          {/* ── Search + filter tabs ── */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text" value={search}
                onChange={e => changeSearch(e.target.value)}
                placeholder="Search URLs…"
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              />
              {search && (
                <button onClick={() => changeSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <FiX size={13} />
                </button>
              )}
            </div>
            <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium shrink-0">
              {["all", "Trained", "Pending", "Failed", "Processing"].map((f) => (
                <button
                  key={f}
                  onClick={() => changeFilter(f)}
                  className={`px-3 py-1.5 transition ${
                    filter === f ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {f === "all" ? "All" : f}
                </button>
              ))}
            </div>
          </div>

          {/* ── Bulk action bar ── */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={handleBulkRetrain}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <FiRefreshCw size={12} /> Retrain Selected
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                >
                  <FiTrash2 size={12} /> Delete Selected
                </button>
                <button onClick={() => setSelectedIds([])} className="text-gray-400 hover:text-gray-600 p-1">
                  <FiX size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Table ── */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="w-10 pl-4 py-3">
                      <input
                        type="checkbox"
                        checked={allPageSel}
                        onChange={toggleAllSel}
                        className="accent-primary"
                      />
                    </th>
                    <th className="px-3 py-3 text-left font-semibold">URL</th>
                    <th className="py-3 text-left font-semibold whitespace-nowrap">Status</th>
                    <th className="py-3 text-left font-semibold">Chars</th>
                    <th className="py-3 text-left font-semibold whitespace-nowrap">Last Trained</th>
                    <th className="py-3 pr-4 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16">
                        <FiLink size={30} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {search || filter !== "all" ? "No URLs match your filters." : "No URLs added yet."}
                        </p>
                        {!search && filter === "all" && (
                          <button
                            onClick={() => setAddMode("single")}
                            className="mt-3 text-sm text-primary hover:underline"
                          >
                            Add your first URL
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : paginated.map((link) => (
                    <tr
                      key={link.id}
                      className={`hover:bg-gray-50 transition ${selectedIds.includes(link.id) ? "bg-primary/5" : ""}`}
                    >
                      <td className="pl-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(link.id)}
                          onChange={() => toggleSel(link.id)}
                          className="accent-primary"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-800 truncate max-w-xs xl:max-w-lg" title={link.url}>
                          {link.url}
                        </p>
                      </td>
                      <td className="py-3 pr-3">
                        <StatusBadge status={link.status} />
                      </td>
                      <td className="py-3 pr-3 text-gray-500 text-xs tabular-nums">
                        {link.chars > 0 ? fmtChars(link.chars) : "—"}
                      </td>
                      <td className="py-3 pr-3 text-gray-500 whitespace-nowrap text-xs">
                        {link.lastTrained}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          {link.status === "Processing" ? (
                            <span className="flex items-center gap-1.5 text-xs text-blue-600 px-3 py-1.5">
                              <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              Training…
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => handleRetrain(link.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                              >
                                <FiRefreshCw size={12} /> Retrain
                              </button>
                              <button
                                onClick={() => handleDelete(link.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                              >
                                <FiTrash2 size={12} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Pagination footer ── */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} links
              </p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}

        </div>
      </div>}
    </div>
  );
}

/* ── Add Content dropdown button ── */
function AddMenu({ addMode, setAddMode }) {
  const [open, setOpen] = useState(false);

  const pick = (key) => { setAddMode(key); setOpen(false); };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
      >
        <FiPlus size={15} /> Add Content <FiChevronDown size={13} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1.5 w-56 bg-white border rounded-xl shadow-lg z-20 py-1.5 overflow-hidden">
            {[
              { key: "single",  icon: <FiLink size={14} />,  label: "Single URL",    desc: "Add one URL at a time" },
              { key: "crawl",   icon: <FiGlobe size={14} />, label: "Crawl Domain",  desc: "Auto-discover all pages" },
              { key: "sitemap", icon: <FiMap size={14} />,   label: "Import Sitemap",desc: "Import from sitemap.xml" },
            ].map(({ key, icon, label, desc }) => (
              <button
                key={key}
                onClick={() => pick(key)}
                className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition"
              >
                <span className="text-primary mt-0.5 shrink-0">{icon}</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400">{desc}</p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, color, dot }) {
  return (
    <div className="bg-white border rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />}
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
